import { styled } from "@mui/material/styles";
import AccordionSummary from "@mui/material/AccordionSummary";
import ListItemButton from "@mui/material/ListItemButton";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  NAV_TEXT_COLOR,
  NAV_FOCUS_COLOR,
  NAV_HOVER_BG_MAIN,
  NAV_HOVER_BG_SUB,
  FONT_FAMILY,
  FONT_WEIGHT,
} from "./navConstants";

// ─── Shared Types ─────────────────────────────────────────────────────────

interface NavTierProps {
  tier?: 1 | 2 | 3;
}

// ─── Reusable Components ──────────────────────────────────────────────────

// Typography variants for different tiers
export const NavText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "tier",
})<NavTierProps>(({ tier = 1 }) => ({
  fontFamily: FONT_FAMILY,
  fontWeight: FONT_WEIGHT,
  color: NAV_TEXT_COLOR,
  fontSize:
    tier === 1 ? "1rem !important" : tier === 2 ? "0.875rem !important" : "0.8125rem !important",
}));

// ─── Tier 1 Accordion Summary (pill-shaped row with icon + label) ─────────
export const NavTier1AccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  height: "auto",
  minHeight: 40,
  // Resolved by the theme's MuiAccordionSummary `&.Mui-expanded` override
  // (see customTheme.ts) so the header doesn't shrink/grow on expand.
  "--nav-accordion-summary-min-height": "40px",
  // margin: `0 0 ${theme.spacing(1.5)}`,
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  flexDirection: "row",
  borderRadius: theme.shape.borderRadius * 3,
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    gap: theme.spacing(1.5),
    margin: 0,
    flexGrow: 1,
    minWidth: 0,
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    marginRight: 0,
  },
  "& .MuiAccordionSummary-content .MuiTypography-root": {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT,
    color: NAV_TEXT_COLOR,
  },
  "&:hover": {
    backgroundColor: NAV_HOVER_BG_MAIN,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${NAV_FOCUS_COLOR}`,
    outlineOffset: "2px",
    backgroundColor: NAV_HOVER_BG_MAIN,
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
}));

// ─── Tier 1 Button (for tier 1 direct links like RxClaim Web) ────────────
export const NavTier1Button = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  height: "auto",
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1.5),
  margin: `0 0 ${theme.spacing(1.5)}`,
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  borderRadius: theme.shape.borderRadius * 3,
  "&:hover": {
    backgroundColor: NAV_HOVER_BG_MAIN,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${NAV_FOCUS_COLOR}`,
    outlineOffset: "2px",
    backgroundColor: NAV_HOVER_BG_MAIN,
  },
}));

// ─── Tier 2 Accordion Summary (no icon, indented) ────────────────────────
export const NavTier2AccordionSummary = styled(AccordionSummary)(
  ({ theme }) => ({
    height: "auto",
    minHeight: 36,
    // Resolved by the theme's MuiAccordionSummary `&.Mui-expanded` override
    // (see customTheme.ts) so the header doesn't shrink/grow on expand.
    "--nav-accordion-summary-min-height": "36px",
    margin: 0,
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    paddingLeft: theme.spacing(2), // alignment under Tier 1 labels
    flexDirection: "row",
    borderRadius: theme.shape.borderRadius * 2,
    "& .MuiAccordionSummary-content .MuiTypography-root": {
      fontFamily: FONT_FAMILY,
      fontWeight: FONT_WEIGHT,
      color: NAV_TEXT_COLOR,
      fontSize: "0.875rem",
    },
    "&:hover": {
      backgroundColor: NAV_HOVER_BG_SUB,
    },
    "&.Mui-focusVisible": {
      outline: `2px solid ${NAV_FOCUS_COLOR}`,
      outlineOffset: "2px",
      backgroundColor: NAV_HOVER_BG_SUB,
    },
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(180deg)",
    },
  }),
);

// ─── Tier 2 List Item Button (direct route at tier 2) ─────────────────────
export const NavTier2ListItem = styled(ListItemButton)(({ theme }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  paddingLeft: theme.spacing(2), // alignment under Tier 1 labels
  borderBottom: "none",
  borderRadius: theme.shape.borderRadius * 2,
  "& .MuiListItemText-primary": {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT,
    color: NAV_TEXT_COLOR,
    fontSize: "0.875rem",
  },
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${NAV_FOCUS_COLOR}`,
    outlineOffset: "2px",
    backgroundColor: NAV_HOVER_BG_SUB,
  },
}));

// ─── Tier 3 List Item Button (deepest level) ─────────────────────────────
export const NavTier3ListItem = styled(ListItemButton)(({ theme }) => ({
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius * 2,
  "& .MuiListItemText-primary": {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT,
    color: NAV_TEXT_COLOR,
    fontSize: "0.8125rem",
  },
  "&:hover": {
    backgroundColor: "transparent",
    "& .MuiListItemText-primary": {
      textDecoration: "underline",
    },
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${NAV_FOCUS_COLOR}`,
    outlineOffset: "2px",
    backgroundColor: "transparent",
  },
}));

// ─── Tier Container (semantic list — rendered as <ul> via component prop) ─
export const NavTierContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tier",
})<NavTierProps>(({ theme, tier }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  ...(tier === 2 && {
    paddingTop: theme.spacing(1),
    // "& > *": {
    //   marginBottom: theme.spacing(0.5),
    // },
    "& > *:last-child": {
      marginBottom: theme.spacing(2.5),
    },
  }),
  ...(tier === 3 && {
    "& > *": {
      marginBottom: theme.spacing(0),
    },
    "& > *:last-child": {
      marginBottom: theme.spacing(0.5),
    },
  }),
}));

// ─── Nav List Item (semantic <li> wrapper for each nav entry) ────────────
export const NavListItem = styled("li")({
  listStyle: "none",
});

// ─── Home Button ──────────────────────────────────────────────────────────
export const NavHomeButton = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  height: 40,
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1.5),
  margin: `0 0 ${theme.spacing(1.5)}`,
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  borderRadius: theme.shape.borderRadius * 3,
  "&:hover": {
    backgroundColor: NAV_HOVER_BG_MAIN,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${NAV_FOCUS_COLOR}`,
    outlineOffset: "0",    
    backgroundColor: NAV_HOVER_BG_MAIN,  
  },
}));
